import {List, Map} from 'immutable'
import {useEffect, useMemo, useRef} from 'react'
import {useSelector} from 'react-redux'
import PropTypes from 'prop-types'

import Loader from '../../../../../shared_components/NewLoader'

import StoryPhotoGallery from './StoryPhotoGallery'
import useEverplanEthicalWills from './hooks/useEverplanEthicalWills'
import useUploadAudio from './hooks/useUploadAudio'
import {MicIcon, PlayIcon, PauseIcon} from './icons'
import {RECORDING_FINISHED_FRACTION, trackLifeStoryEvent} from './lifeStoryEventLogger'
import {
  DEFAULT_LIFE_STORY_TITLE,
  LIFE_AND_LEGACY_CATEGORY_PATH,
  everplanPreviewStoryExitPath,
  formatDuration,
  storiesByChapter
} from './lifeStoryTools'
import {chapterCountLabel, paragraphsFromText, storyDateline} from '../../../../../lib/lifeStoryDisplay'
import {currentUserConfig} from '../../../../../lib/userTools'
import {everplanOwners, isOwnEverplan} from '../../../../../lib/plan_data/userData'
import {firstName, humanizedFullNames} from '../../../../../lib/nameTools'
import {useDeputizerData} from '../../../../../lib/hooks'
import routerUtils from '../../../../../lib/routerUtils'

import './lifeStory.scss'

const WAVEFORM_BAR_COUNT = 46

/** How close to the bottom of the page counts as having read to the end. */
const STORY_END_THRESHOLD_PX = 80

/** Deterministic pseudo-random bar heights (mock's LCG), seeded per recording
 * so each waveform looks distinct but stays stable across renders. */
const waveformBars = seedText => {
  let seed = 0

  String(seedText).split('').forEach(char => {
    seed = ((seed * 31) + char.charCodeAt(0)) % 233280
  })
  seed = seed || 53

  const nextRandom = () => {
    seed = ((seed * 9301) + 49297) % 233280

    return seed / 233280
  }

  const barWidth = 100 / WAVEFORM_BAR_COUNT

  return Array.from({length: WAVEFORM_BAR_COUNT}, (ignored, index) => {
    const height = 18 + (nextRandom() * 74)

    return {
      x: (index * barWidth) + (barWidth * 0.18),
      y: 50 - (height / 2),
      width: barWidth * 0.55,
      height
    }
  })
}

const SEEK_KEY_STEP = 0.05

const Waveform = ({seed, progress, onSeek}) => {
  const bars = useMemo(() => waveformBars(seed), [seed])
  const clipId = `ls-wave-clip-${seed}`

  const seekFromClick = event => {
    const rect = event.currentTarget.getBoundingClientRect()

    onSeek((event.clientX - rect.left) / rect.width)
  }

  const seekFromKey = event => {
    const seekTargets = {
      ArrowLeft: progress - SEEK_KEY_STEP,
      ArrowDown: progress - SEEK_KEY_STEP,
      ArrowRight: progress + SEEK_KEY_STEP,
      ArrowUp: progress + SEEK_KEY_STEP,
      Home: 0,
      End: 1
    }

    if (!(event.key in seekTargets)) return

    event.preventDefault()
    onSeek(Math.max(0, Math.min(1, seekTargets[event.key])))
  }

  const rects = bars.map((bar, index) => (
    <rect fill='currentColor' height={bar.height} key={index} rx='1.4' width={bar.width} x={bar.x} y={bar.y} />
  ))

  return (
    <div
      aria-label='Seek within recording'
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.round(progress * 100)}
      aria-valuetext={`${Math.round(progress * 100)}% played`}
      className='ls-waveform'
      role='slider'
      tabIndex={0}
      onClick={seekFromClick}
      onKeyDown={seekFromKey}>
      <svg aria-hidden='true' preserveAspectRatio='none' viewBox='0 0 100 100'>
        <g className='ls-wave-rest'>{rects}</g>
        <clipPath id={clipId}>
          <rect height='100' width={progress * 100} x='0' y='0' />
        </clipPath>
        <g className='ls-wave-played' clipPath={`url(#${clipId})`}>{rects}</g>
      </svg>
    </div>
  )
}

Waveform.propTypes = {
  onSeek: PropTypes.func,
  progress: PropTypes.number,
  seed: PropTypes.string
}

const VoiceCard = ({recording, voiceLabel, audio, onPlay}) => {
  const uploadId = recording['upload-id']
  const activeTrack = audio.track && audio.track.uploadId === uploadId ? audio.track : null
  const totalDuration = (activeTrack && activeTrack.duration) || recording.duration || 0
  const durationLabel = activeTrack && activeTrack.playing ?
    formatDuration(Math.max(0, totalDuration - activeTrack.elapsed)) :
    formatDuration(totalDuration)

  const togglePlay = () => {
    if (!(activeTrack && activeTrack.playing)) onPlay()

    if (activeTrack)
      audio.togglePlayback()
    else
      audio.play({uploadId, filename: recording.filename, duration: recording.duration || 0})
  }

  const seek = fraction => {
    if (activeTrack)
      audio.seek(fraction)
    else
      togglePlay()
  }

  return (
    <div className='ls-voice-card'>
      <div className='ls-voice-head'>
        <MicIcon size={18} strokeWidth={2} />
        <span className='ls-voice-label'>{voiceLabel}</span>
        <span className='ls-voice-dur'>{durationLabel}</span>
      </div>
      <div className='ls-voice-row'>
        <button
          aria-label={activeTrack && activeTrack.playing ? 'Pause recording' : 'Play recording'}
          className='ls-story-play-btn'
          onClick={togglePlay}>
          {activeTrack && activeTrack.playing ? <PauseIcon size={20} /> : <PlayIcon size={20} />}
        </button>
        <Waveform
          onSeek={seek}
          progress={activeTrack && totalDuration > 0 ? activeTrack.elapsed / totalDuration : 0}
          seed={uploadId}
        />
      </div>
    </div>
  )
}

VoiceCard.propTypes = {
  audio: PropTypes.shape({
    play: PropTypes.func,
    seek: PropTypes.func,
    togglePlayback: PropTypes.func,
    track: PropTypes.object
  }),
  onPlay: PropTypes.func,
  recording: PropTypes.object,
  voiceLabel: PropTypes.string
}

const StoryBlock = ({story, voiceLabel, audio, onPlay}) => (
  <div className='ls-story-block'>
    <div className='ls-story-question'>{story.question}</div>
    {
      // Under the question, above the voice recording and the words
      // (TK-3108, AC 5.2).
      story.photos && story.photos.length > 0 && <StoryPhotoGallery photos={story.photos} />
    }
    {story.recording && <VoiceCard audio={audio} onPlay={onPlay} recording={story.recording} voiceLabel={voiceLabel} />}
    {
      Boolean((story.text || '').trim()) &&
        <div className='ls-story-answer'>
          {
            paragraphsFromText(story.text).map((paragraph, paragraphIndex) => (
              <p key={paragraphIndex}>
                {
                  paragraph.split('\n').map((line, lineIndex) => (
                    <span key={lineIndex}>
                      {lineIndex > 0 && <br />}
                      {line}
                    </span>
                  ))
                }
              </p>
            ))
          }
        </div>
    }
  </div>
)

StoryBlock.propTypes = {
  audio: PropTypes.object,
  onPlay: PropTypes.func,
  story: PropTypes.object,
  voiceLabel: PropTypes.string
}

/**
 * Full-page reading view of a published Life Story (mock's story stage):
 * chapters of questions and answers with inline voice recordings. Owners read
 * their own story at `cm/life-story/:ethicalWillId/story`; the everplan
 * preview (deputies, or the owner previewing their plan) reaches it at
 * `cm/everplan-preview/:everplanId/life-story/:ethicalWillId/story`, and the
 * copy adapts to whether the reader is the author.
 */
const LifeStoryStoryScreen = props => {
  const previewEverplanId = props.params.everplanId ? parseInt(props.params.everplanId, 10) : null

  const owners = useSelector(state => state.owners)
  const userConfig = useSelector(state => currentUserConfig(state.api))
  const viewerIsOwner = !previewEverplanId || isOwnEverplan({everplanPreviewId: props.params.everplanId, owners})

  const {loadingDeputizers, deputizers} = useDeputizerData(userConfig)
  const deputyOwnership = viewerIsOwner || loadingDeputizers ?
    null :
    deputizers.find(deputizer => deputizer.get('everplan-id') === previewEverplanId)

  const {loadingEthicalWills, ethicalWills} = useEverplanEthicalWills({
    everplanId: props.params.everplanId,
    ownEverplan: viewerIsOwner
  })

  const audio = useUploadAudio()

  const ethicalWill = ethicalWills.find(will => will.get('id') === props.params.ethicalWillId)

  // Preview readers go back to the Life Story item modal they entered from,
  // not the bare everplan (TK-3059).
  const exitPath = previewEverplanId ?
    everplanPreviewStoryExitPath({
      everplanId: previewEverplanId,
      firstGroupId: ethicalWill && ethicalWill.get('first-group-id')
    }) :
    LIFE_AND_LEGACY_CATEGORY_PATH
  const exit = () => routerUtils.push(exitPath)

  // A stale link (discarded story, revoked permission) exits back to the
  // Everplan instead of rendering an empty shell. Failed fetches land here
  // too: retrying is one click away from the Everplan.
  const unknownEthicalWill = !loadingEthicalWills && !ethicalWill
  // A deputy with no ownership for this everplan (revoked access, stale link,
  // or a failed deputizer query yielding no match) is treated the same way:
  // exit rather than load forever.
  const unknownOwnership = !viewerIsOwner && !loadingDeputizers && !deputyOwnership

  useEffect(() => {
    if (unknownEthicalWill || unknownOwnership)
      routerUtils.push(exitPath)
  }, [unknownEthicalWill, unknownOwnership, exitPath])

  const waitingOnOwnership = !viewerIsOwner && (loadingDeputizers || !deputyOwnership)
  const storyShown = !(loadingEthicalWills || unknownEthicalWill || waitingOnOwnership)

  // How deputies read a shared story (TK-2993); the author reading their own
  // isn't part of that picture.
  const trackDeputyEvent = eventName => {
    if (!viewerIsOwner) trackLifeStoryEvent({eventName, userConfig, viewerIsOwner})
  }

  // Reading to the end is logged once per visit. A story short enough to fit
  // on screen is read to the end as soon as it shows.
  const storyRef = useRef(null)
  const finishLoggedRef = useRef(false)

  const logFinishAtEnd = () => {
    const node = storyRef.current

    if (!node || finishLoggedRef.current) return

    if (node.scrollTop + node.clientHeight >= node.scrollHeight - STORY_END_THRESHOLD_PX) {
      finishLoggedRef.current = true
      trackDeputyEvent('finish_my_story')
    }
  }

  useEffect(() => {
    if (storyShown) logFinishAtEnd()
  }, [storyShown])

  // Each recording counts as finished once, the first time the deputy has
  // heard RECORDING_FINISHED_FRACTION of it. What's been heard, not where the
  // playhead is: seeking near the end isn't listening to it.
  const finishedRecordingsRef = useRef(new Set())

  useEffect(() => {
    const track = audio.track

    if (!track || !track.duration || finishedRecordingsRef.current.has(track.uploadId)) return

    if (track.played / track.duration >= RECORDING_FINISHED_FRACTION) {
      finishedRecordingsRef.current.add(track.uploadId)
      trackDeputyEvent('finish_my_story_audio')
    }
  }, [audio.track])

  if (loadingEthicalWills || unknownEthicalWill || waitingOnOwnership)
    return <Loader loading={true} />

  const ownerships = everplanOwners({ownership: deputyOwnership, ownEverplan: viewerIsOwner, owners})
  const ownerFirstName = firstName(ownerships.first() || Map())
  // humanizedFullNames throws on an empty list, which the owners store can be
  // before its data lands on the owner's own route (where the name is unused).
  const ownerFullName = ownerships.isEmpty() ? '' : humanizedFullNames(ownerships)

  const title = ethicalWill.getIn(['data', 'title']) || DEFAULT_LIFE_STORY_TITLE
  const stories = (ethicalWill.getIn(['data', 'stories']) || List()).toJS()
  const chapters = storiesByChapter(stories)

  const possessive = ownerFirstName ? `${ownerFirstName}\u2019s` : 'their'
  // The back nav names the plan's owner in full (TK-3059), top and bottom.
  const backLabel = viewerIsOwner ?
    'Back to my Everplan' :
    `Back to ${ownerFullName ? `${ownerFullName}\u2019s` : 'their'} Everplan`
  const dateline = viewerIsOwner ? 'as told by you' : storyDateline(ownerFullName || 'your loved one')
  const note = viewerIsOwner ?
    'This is your story as your loved ones will read it. Where they see the voice mark, they\u2019ll hear it in your own voice.' :
    'Read at your own pace and come back anytime. Where you see the voice mark, you\u2019ll hear it in their own voice.'
  const voiceLabel = viewerIsOwner ? 'Listen in your own voice' : `Listen in ${possessive} voice`

  return (
    <div className='life-story-story' onScroll={logFinishAtEnd} ref={storyRef}>
      <div className='ls-story-page'>
        <button className='ls-story-exit' onClick={exit}>&larr; {backLabel}</button>
        <header className='ls-story-head'>
          <div className='ls-story-eyebrow'>My Life Story</div>
          <h1>{title}</h1>
          <div className='ls-story-dateline'>{dateline}</div>
          <p className='ls-story-note'>{note}</p>
        </header>
        {
          chapters.map(({chapter, stories: chapterStories}, chapterIndex) => (
            <section className='ls-story-chapter' key={chapter.key}>
              <div className='ls-chapter-head'>
                <h2 className='ls-chapter-title'>{chapter.name}</h2>
                <span className='ls-chapter-count'>{chapterCountLabel({index: chapterIndex, storyCount: chapterStories.length})}</span>
              </div>
              {
                chapterStories.map(story => (
                  <StoryBlock
                    audio={audio}
                    key={story['prompt-id']}
                    onPlay={() => trackDeputyEvent('click_audio_play')}
                    story={story}
                    voiceLabel={voiceLabel}
                  />
                ))
              }
            </section>
          ))
        }
        <div className='ls-story-end'>
          <div aria-hidden='true' className='ls-story-end-dots'>&middot; &middot; &middot;</div>
          <button className='ls-story-exit' onClick={exit}>&larr; {backLabel}</button>
        </div>
      </div>
    </div>
  )
}

LifeStoryStoryScreen.propTypes = {
  params: PropTypes.shape({
    ethicalWillId: PropTypes.string,
    everplanId: PropTypes.string
  })
}

export default LifeStoryStoryScreen
